import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { get } from "lodash";
import {
  Field,
  Fields,
  FieldArray,
  FormSection,
  formValueSelector
} from "redux-form";
import { connect } from "react-redux";
import { Row, Col } from "react-bootstrap";
import onSuccess from "LIB/submission/onSuccess";
import {
  form,
  Form as AppForm,
  Errors,
  Select,
  FormInput,
  Check,
  CurrencyField,
  NumberInput,
  ShippingMethod
} from "MODULES/shared/components/Forms";
import * as F from "MODULES/shared/components/Forms/FormTypes";
import {
  QUALITY_CONTROL_MANAGER,
  WAREHOUSE,
  WAREHOUSE_EXPERT,
  WAREHOUSE_LOGISTICS_DIRECTOR,
  WAREHOUSE_MANAGER
} from "MODULES/users/constants";
import {
  ATTACHMENT_TYPES,
  OUTGOING_AWB_NUMBER,
  READ_ONLY_COMPANY,
  READ_ONLY_COMPANY_UUID,
  READ_ONLY_INVENTORY_UUID,
  READ_ONLY_PART_NUMBER,
  READ_ONLY_PART_UUID,
  READ_ONLY_SERIAL_NUMBER,
  SHIP_MULTIPLE_ITEM_IDS
} from "MODULES/Shipping/constants";
import { create } from "MODULES/Shipping/actions";
import countries from "LIB/countries";
import AdditionalDetails from "MODULES/SalesOrders/components/shared/FormSections/ShippingSection/AdditionalDetails";
import AttachmentListSection from "MODULES/Receiving/components/shared/FormSections/AttachmentListSection";
import WeightAndDimensionsSection from "MODULES/parts/components/shared/FormSections/WeightAndDimensionsSection";
import NetWeightAndDimsFormRow from "MODULES/shared/components/Forms/NetWeightAndDimsFormRow/NetWeightAndDimsFormRow";
import Warning from "MODULES/shared/components/Warning/Warning";
import { Authorized, PartNumber } from "MODULES/shared/components";
import {
  COUNTRY_OF_MFG,
  BIO_HAZMAT,
  HAZMAT,
  HEIGHT,
  LENGTH,
  WEIGHT,
  WEIGHT_UNIT,
  WIDTH
} from "MODULES/parts/constants";
import {
  API_BASE,
  ATTACHMENTS,
  CARRIER,
  COMPANY,
  DISCREET,
  QUANTITY,
  SHIPPING_METHOD
} from "APP_ROOT/constants";
import FormRow from "MODULES/shared/components/Forms/FormRow";
import {
  DOWNLOAD_PACKING_SLIP,
  SEND_WEIGHTS_AND_DIMS
} from "MODULES/shared/components/CommercialInvoice/helpers";
import {
  SHIPMENT,
  MISTAKE_SHIPMENT,
  RMA,
  RECEIVED_VIRTUALLY
} from "MODULES/Receiving/constants";
import FormLink from "MODULES/shared/components/Forms/FormLink/FormLink";
import { Link } from "react-router-dom";
import { hasAnyOfSubroles } from "MODULES/users/helpers";
import { useCurrentUser } from "APP_ROOT/hooks";
import { properCase } from "LIB/formatters/text";
import Save from "MODULES/Shipping/components/Dashboard/Create/Save";
import ShipMultipleRecordsTable from "MODULES/Shipping/components/Dashboard/Create/ShipMultipleRecordsTable";
import validate from "./validate";
import {
  MRO_ITEM,
  PO_ITEM,
  REJECTED_CORE_ORDER,
  RETURN_TO_CUSTOMER_RECORD,
  RETURNED_CONSIGNMENT,
  RETURNED_CORE,
  RO_ITEM,
  SO_ITEM,
  TRANSFER
} from "../../../../Orders/shared/constants";
import { withFieldsFromFormWithKeys } from "../../../../shared/decorators/withFieldsFromForm";

export const formName = "createShippingRecord";

const ShippingMethodWarning = ({ shippingMethod, carrier, method }) => {
  if (!shippingMethod) return null;

  if (
    shippingMethod.carrier === carrier &&
    shippingMethod.shipping_method === method
  )
    return null;

  return (
    <Warning>
      SHIPPING CARRIER OR METHOD DO NOT MATCH SALES ORDER. PLEASE VERIFY.
    </Warning>
  );
};

ShippingMethodWarning.propTypes = {
  shippingMethod: PropTypes.shape({}),
  carrier: PropTypes.string,
  method: PropTypes.string
};

const hasShippingMethod = type =>
  [
    "managed_repair_order_line_item",
    "mistake_shipment",
    PO_ITEM,
    "rejected_core_order",
    RO_ITEM,
    RETURNED_CONSIGNMENT,
    RETURNED_CORE,
    "rma",
    SO_ITEM,
    TRANSFER
  ].includes(type);

function HazmatFields({ hazmat, bio_hazmat }) {
  return (
    <div className="form-group">
      <label htmlFor="warnings" className="control-label">
        Line Hazmat
      </label>
      <br />
      <div
        id="warnings"
        className="btn-group btn-group-justified"
        data-toggle="buttons"
      >
        <Check input={hazmat.input} label="Hazmat" icon="fa-warning" />
        <Check input={bio_hazmat.input} label="Bio-Hazmat" icon="fa-flask" />
      </div>
    </div>
  );
}

function PrintPackingSlip({ packingUrl, shippingRecord }) {
  return shippingRecord.release_for_shipment ? (
    <a
      href={packingUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="btn btn-success"
      style={{ paddingLeft: "5px", paddingRight: "5px" }}
    >
      Print packing slip
    </a>
  ) : (
    <button type="button" disabled className="btn btn-success">
      Print Packing Slip
    </button>
  );
}

PrintPackingSlip.propTypes = {
  packingUrl: PropTypes.string,
  shippingRecord: PropTypes.shape({})
};

HazmatFields.propTypes = {
  hazmat: F.formField(F.boolInput).isRequired,
  bio_hazmat: F.formField(F.boolInput).isRequired
};

const orBlank = val => val || "";

export const SimpleCopy = ({ value }) => (
  <span
    className="fa fa-copy"
    style={{
      cursor: "pointer",
      marginLeft: "7px",
      zIndex: 1000
    }}
    onClick={() => {
      navigator.clipboard.writeText(value || "");
    }}
  />
);

SimpleCopy.propTypes = {
  value: PropTypes.string.isRequired
};

export function Form(props) {
  const {
    accountNumber,
    carrier,
    getField,
    handleSubmit,
    close,
    onSubmit,
    submitting,
    discreet,
    errorMessages,
    length,
    width,
    weight,
    weightUnit,
    countryOfMfg,
    height,
    outgoingAwbNumber,
    parentRecord,
    submitFailed,
    shippingRecord,
    shippingMethod,
    setCurrentForm,
    setWeightsAndDims,
    loadingParentRecord,
    multipleShipments,
    setPackingUrl,
    shippingAddressWarning,
    packingUrl
  } = props;

  const countryOptions = countries.map(c => ({
    value: c.code,
    text: c.name
  }));

  useEffect(
    () => {
      setPackingUrl(`${API_BASE.replace("/v1", "") +
        shippingRecord.packing_slip_url}?allow_warehouse_basic=true&account_number=${accountNumber ||
        ""}&carrier=${carrier || ""}&shipping_method=${shippingMethod ||
        ""}${discreet
        ? "&discreet=true"
        : ""}&inventory_id=${shippingRecord.inventory_id}
                &weight=${weight || ""}&weight_unit=${weightUnit ||
        ""}&length=${length || ""}&width=${width || ""}&height=${height ||
        ""}&country_of_mfg=${orBlank(
        countryOfMfg
      )}&outgoing_awb_number=${orBlank(outgoingAwbNumber)}`);

      return function cleanup() {};
    },
    [
      shippingRecord.packing_slip_url,
      accountNumber,
      carrier,
      shippingMethod,
      discreet,
      shippingRecord.inventory_id,
      weight,
      weightUnit,
      length,
      outgoingAwbNumber,
      width,
      height,
      countryOfMfg
    ]
  );

  return (
    <AppForm {...props}>
      <AppForm.Body>
        {![SO_ITEM, RO_ITEM, MRO_ITEM, TRANSFER].includes(
          shippingRecord.type
        ) && (
          <FormRow>
            <Col md={6}>
              <FormLink
                label="Part #"
                addons={<SimpleCopy value={getField(READ_ONLY_PART_NUMBER)} />}
              >
                <PartNumber
                  part={{
                    part_number: getField(READ_ONLY_PART_NUMBER),
                    uuid: getField(READ_ONLY_PART_UUID)
                  }}
                  statsTooltip={false}
                />
              </FormLink>
            </Col>
            <Col md={6}>
              <FormLink label="Serial #">
                <Link to={`/inventories/${getField(READ_ONLY_INVENTORY_UUID)}`}>
                  <span>{getField(READ_ONLY_SERIAL_NUMBER)}</span>
                </Link>
              </FormLink>
            </Col>
          </FormRow>
        )}
        {(get(shippingRecord, "parent.id") ||
          shippingRecord.type === TRANSFER) && (
          <ShipMultipleRecordsTable
            formName={formName}
            orderId={get(shippingRecord, "parent.id") || shippingRecord.id}
            type={shippingRecord.type}
          />
        )}
        <Row>
          <Col md={6}>
            <FormLink
              label={COMPANY}
              addons={<SimpleCopy value={getField(READ_ONLY_COMPANY)} />}
            >
              <Link to={`/companies/${getField(READ_ONLY_COMPANY_UUID)}`}>
                <span>{getField(READ_ONLY_COMPANY)}</span>
              </Link>
            </FormLink>
          </Col>
          <Col md={6}>
            <Field
              name="read_only_customer_po"
              label={[
                "Customer PO #",
                <SimpleCopy value={getField("read_only_customer_po")} />
              ]}
              component={FormInput}
              disabled
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <label
              className="control-label"
              htmlFor={"read_only_customer_name"}
            >
              Cust Contact
            </label>
            <input
              className="form-control"
              name="read_only_customer_name"
              type="text"
              value={get(parentRecord, "contact.name")}
              disabled
            />
          </Col>
          <Col md={6}>
            <FormLink
              label="Customer e-mail"
              addons={
                <SimpleCopy value={getField("read_only_customer_email")} />
              }
            >
              <a href={`mailto:${getField("read_only_customer_email")}`}>
                {getField("read_only_customer_email")}
              </a>
            </FormLink>
          </Col>
        </Row>
        {shippingRecord.type === SO_ITEM && (
          <Row>
            <Col md={6}>
              <Field
                name="read_only_salesperson"
                label="Salesperson"
                component={FormInput}
                disabled
              />
            </Col>
            <Col md={6}>
              <CurrencyField
                name="read_only_sales_ea"
                label="Sales EA"
                inputProps={{ disabled: true }}
              />
            </Col>
          </Row>
        )}
        <Row>
          <Col md={6}>
            <Field
              component={Select}
              options={countryOptions}
              name={COUNTRY_OF_MFG}
              label="Ctry of Mfg"
            />
          </Col>
        </Row>
        <h4>Gross (Package) Weight & Dims</h4>
        <WeightAndDimensionsSection
          dimensionsRequired={!shippingRecord[RECEIVED_VIRTUALLY]}
          weightRequired={!shippingRecord[RECEIVED_VIRTUALLY]}
          weightLabel="Gross (Package) Weight"
        />
        <NetWeightAndDimsFormRow getField={getField} />
        <hr className="hr-xs hr-dashed" />
        {parentRecord &&
          shippingRecord.type === SO_ITEM && (
            <ShippingMethodWarning
              shippingMethod={parentRecord.ship_to_shipping_method}
              carrier={getField([SHIPPING_METHOD, CARRIER].join("."))}
              method={getField([SHIPPING_METHOD, SHIPPING_METHOD].join("."))}
            />
          )}
        {shippingRecord &&
          hasShippingMethod(shippingRecord.type) && (
            <FormSection name={SHIPPING_METHOD}>
              <ShippingMethod
                disabled={
                  !hasAnyOfSubroles(useCurrentUser(), [
                    WAREHOUSE_MANAGER,
                    WAREHOUSE_EXPERT,
                    WAREHOUSE_LOGISTICS_DIRECTOR,
                    QUALITY_CONTROL_MANAGER
                  ])
                }
                required
              />
            </FormSection>
          )}
        <Row className="row-col-gutters-half">
          <Col md={4}>
            <Field
              name={OUTGOING_AWB_NUMBER}
              label={[
                "Outgoing AWB #",
                <SimpleCopy value={getField(OUTGOING_AWB_NUMBER)} />
              ]}
              component={FormInput}
              required
            />
          </Col>
          {(!multipleShipments || multipleShipments.length < 2) && (
            <Col md={2}>
              <Field
                name={QUANTITY}
                label={properCase(QUANTITY)}
                component={NumberInput}
                roundDigits={0}
                required
              />
            </Col>
          )}
          <Col md={6}>
            <Fields names={[HAZMAT, BIO_HAZMAT]} component={HazmatFields} />
          </Col>
        </Row>
        <hr className="hr-xs hr-dashed" />
        <AdditionalDetails
          lineItem={shippingRecord}
          showDiscreet={shippingRecord.type !== PO_ITEM}
        />
        <hr className="hr-xs hr-dashed" />
        <FieldArray
          name={ATTACHMENTS}
          component={AttachmentListSection}
          attachmentTypes={ATTACHMENT_TYPES}
          getField={getField}
        />
        {shippingAddressWarning && (
          <Warning>
            A Primary Shipping Address for the Customer is necessary to allow
            for creation of the Commercial Invoice.
          </Warning>
        )}
      </AppForm.Body>
      <AppForm.Footer>
        {submitFailed && <Errors errors={errorMessages} />}
        <Authorized roles={[WAREHOUSE]}>
          {shippingRecord.type === SO_ITEM && (
            <button
              className="btn btn-primary"
              type="button"
              disabled={!shippingRecord.release_for_shipment}
              onClick={e => {
                e.preventDefault();
                setWeightsAndDims({
                  weight_unit: props.weightUnit,
                  [WEIGHT]: Number(props[WEIGHT]),
                  [WIDTH]: Number(props[WIDTH]),
                  [LENGTH]: Number(props[LENGTH]),
                  [HEIGHT]: Number(props[HEIGHT])
                });
                setCurrentForm(SEND_WEIGHTS_AND_DIMS);
              }}
            >
              Send Wt & DIMs
            </button>
          )}
        </Authorized>
        {shippingRecord.type === RO_ITEM && (
          <a
            href={`${API_BASE}/repair_orders/${shippingRecord.parent
              .id}/download`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-success"
            style={{ paddingLeft: "5px", paddingRight: "5px" }}
          >
            Print RO
          </a>
        )}
        {(shippingRecord.can_print_packing_slip &&
          (!loadingParentRecord &&
          parentRecord &&
          ![
            RO_ITEM,
            RMA,
            REJECTED_CORE_ORDER,
            RETURN_TO_CUSTOMER_RECORD,
            RETURNED_CONSIGNMENT
          ].includes(shippingRecord.type) &&
          parentRecord.line_items.length > 1 ? (
            <button
              className="btn btn-success"
              type="button"
              onClick={() => setCurrentForm(DOWNLOAD_PACKING_SLIP)}
              disabled={!shippingRecord.release_for_shipment}
            >
              Print Packing Slip
            </button>
          ) : (
            !loadingParentRecord &&
            shippingRecord.type !== RO_ITEM && (
              <PrintPackingSlip
                shippingRecord={shippingRecord}
                packingUrl={packingUrl}
              />
            )
          ))) ||
          (shippingRecord.type === MISTAKE_SHIPMENT && (
            <PrintPackingSlip
              shippingRecord={shippingRecord}
              packingUrl={`${API_BASE}/mistake_shipments/${shippingRecord.order_id}/download_packing_slip`}
            />
          )) ||
          (shippingRecord.type === SHIPMENT && (
            <PrintPackingSlip
              shippingRecord={shippingRecord}
              packingUrl={`${API_BASE}/shipments/${shippingRecord.order_id}/download_packing_slip`}
            />
          )) ||
          (shippingRecord.type !== RO_ITEM && (
            <button
              className="btn btn-success"
              style={{ paddingLeft: "5px", paddingRight: "5px" }}
              disabled
            >
              Print packing slip
            </button>
          ))}
        <button
          type="button"
          className="btn btn-default"
          style={{ paddingLeft: "5px", paddingRight: "5px" }}
          onClick={close}
        >
          Cancel
        </button>
        <Save {...props} />
        <button
          disabled={submitting || !shippingRecord.release_for_shipment}
          className="btn btn-primary"
          style={{ paddingLeft: "5px", paddingRight: "5px" }}
          onClick={onSuccess(
            handleSubmit(values =>
              onSubmit({
                formData: { ...values },
                lineItem: shippingRecord
              })
            ),
            close,
            props
          )}
        >
          {submitting ? (
            <i className="fa fa-cog fa-spin fa-fw fa-lg" />
          ) : (
            "Ship"
          )}
        </button>
      </AppForm.Footer>
    </AppForm>
  );
}

Form.propTypes = {
  accountNumber: PropTypes.string,
  carrier: PropTypes.string,
  close: PropTypes.func.isRequired,
  errorMessages: PropTypes.arrayOf(PropTypes.string),
  length: PropTypes.string,
  width: PropTypes.string,
  height: PropTypes.string,
  countryOfMfg: PropTypes.string,
  weight: PropTypes.string,
  weightUnit: PropTypes.string,
  discreet: PropTypes.bool,
  handleSubmit: PropTypes.func.isRequired,
  loadingParentRecord: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  outgoingAwbNumber: PropTypes.string.isRequired,
  packingUrl: PropTypes.number,
  parentRecord: PropTypes.shape({}),
  setCurrentForm: PropTypes.func.isRequired,
  submitFailed: PropTypes.bool,
  submitting: PropTypes.bool.isRequired,
  shippingRecord: PropTypes.shape({}),
  shippingMethod: PropTypes.string
};

const selector = formValueSelector(formName);

export default connect(
  state => ({
    getField: field => selector(state, field)
  }),
  {
    onSubmit: create
  }
)(
  form({
    form: formName,
    validate
  })(
    withFieldsFromFormWithKeys(formName, {
      accountNumber: "shipping_method.account_number",
      carrier: "shipping_method.carrier",
      countryOfMfg: COUNTRY_OF_MFG,
      discreet: DISCREET,
      length: LENGTH,
      outgoingAwbNumber: OUTGOING_AWB_NUMBER,
      width: WIDTH,
      weight: WEIGHT,
      weightUnit: WEIGHT_UNIT,
      height: HEIGHT,
      shippingMethod: "shipping_method.shipping_method",
      multipleShipments: SHIP_MULTIPLE_ITEM_IDS
    })(Form)
  )
);



// WEBPACK FOOTER //
// ./app/modules/Shipping/components/Dashboard/Create/Form.jsx